/** Wrapper returned by httpClient — just the parsed response body */
export type ApiResponse<T> = T;

/** Standard API error response — used by error handling in service callers */
export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: string[];
  };
}

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ApiError).error?.code === 'string'
  );
}

/** Onboarding status from GET /api/onboarding */
export interface OnboardingStatus {
  _id?: string;
  completed: boolean;
  currentStep: number;
  data: OnboardingResponse | null;
}

/** Onboarding response from backend API (matches backend schema) */
export interface OnboardingResponse {
  /** 1-3 niches (LIL-426): library ids or the user's own text. Empty when unset. */
  niche: string[];
  platforms: PlatformUrls;
  goals: string[];
  contentTopics: string[];
  automationStatus: AutomationStatus;
  automationStartedAt: string | null;
  automationCompletedAt: string | null;
}

export interface PlatformUrls {
  linkedin?: string;
  x?: string;
  facebook?: string;
}

export type AutomationStatus =
  | 'not_started'
  | 'analyzing_niche'
  | 'indexing_knowledge_base'
  | 'understanding_goals'
  | 'calibrating_tone'
  | 'finalizing_setup'
  | 'completed'
  | 'failed';

/** Request body for PUT /api/onboarding */
export interface SaveOnboardingRequest {
  /** 1-3 library ids or custom niches (LIL-426). Omit when nothing is selected. */
  niche?: string[];
  platforms?: PlatformUrls;
  goals?: string[];
  currentStep?: number;
}

/** Response from POST /api/onboarding/automation/start */
export interface StartAutomationResult {
  jobId: string;
  status: 'in_progress';
  /** Estimated duration in seconds */
  estimatedDuration: number;
}

/** Response from GET /api/onboarding/automation/status */
export interface AutomationStatusResponse {
  status: AutomationStatus;
  startedAt: string | null;
  completedAt: string | null;
  stages: AutomationStageInfo[];
  /**
   * Post writing continues after the automation reports `completed` (LIL-492);
   * these counters are how a client sees that work. Absent until the first
   * post is queued.
   */
  contentGeneration?: ContentGenerationStatus;
}

/** Individual stage info from automation status */
export interface AutomationStageInfo {
  name: string;
  status: 'pending' | 'processing' | 'completed' | 'skipped' | 'error';
  progress: number;
  message: string;
}

/** Live counters of the post-completion content generation (LIL-492 / LIL-535). */
export interface ContentGenerationStatus {
  total: number;
  generated: number;
  failed: number;
  status: AutomationStageInfo['status'];
}

/** Response from GET /api/users/me — current user's profile */
export interface UserProfile {
  _id: string;
  name: string;
  email: string;
  emailVerified?: boolean;
  image?: string;
  onboardingStatus?: string;
  onboardingCompleted?: boolean;
  /** Bytes of storage the user has consumed (quota is a client-side constant) */
  storageUsedBytes?: number;
}

/** File upload progress callback */
export type UploadProgressCallback = (percentage: number) => void;

/** File record returned by POST /api/users/me/avatar */
export interface AvatarFile {
  _id: string;
  source: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  gcsPath: string;
  publicUrl: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/** Automation stage names (matches backend HIV-78 OnboardingResponse model) */
export type AutomationStage =
  | 'analyzing_niche'
  | 'indexing_knowledge_base'
  | 'understanding_goals'
  | 'calibrating_tone'
  | 'finalizing_setup';

/** Stage tracking for each automation stage */
export interface StageStatus {
  stage: AutomationStage;
  status: 'pending' | 'processing' | 'completed' | 'error' | 'skipped';
  progress?: number;
  message?: string;
  startedAt?: string;
  completedAt?: string;
}

/** File uploaded during onboarding */
export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  progress: number;
  status: 'uploading' | 'completed' | 'failed';
}

/** Automation error */
export interface AutomationError {
  stage: AutomationStage;
  message: string;
  recoverable: boolean;
}

/** Knowledge base document from GET /api/knowledge-base */
export interface KnowledgeBaseDocument {
  _id: string;
  type: 'file' | 'url' | 'text';
  title: string;
  content?: string;
  status: 'pending' | 'processing' | 'indexed' | 'failed';
  url?: string;
  fileId?: string | { _id: string; publicUrl?: string; originalName?: string; mimeType?: string };
  errorMessage?: string;
  createdAt: string;
}

/** Response from POST /api/knowledge-base/files */
export interface UploadFileResponse {
  kbDocument: KnowledgeBaseDocument;
  file: {
    _id: string;
    publicUrl?: string;
    originalName?: string;
    mimeType?: string;
    sizeBytes?: number;
  };
}

/** Request body for POST /api/knowledge-base/urls(/check) */
export interface AddKnowledgeBaseUrlRequest {
  url: string;
}

/** Response from POST /api/knowledge-base/urls/check */
export interface CheckUrlResponse {
  accessible: boolean;
  message?: string;
}

/** Response from POST /api/knowledge-base/urls */
export interface AddUrlResponse {
  kbDocument: KnowledgeBaseDocument;
}

/** Request body for POST /api/knowledge-base/text — title ≤ 200, content ≤ 50000 */
export interface AddKnowledgeBaseTextRequest {
  title: string;
  content: string;
}

/** Response from POST /api/knowledge-base/text */
export interface AddTextResponse {
  kbDocument: KnowledgeBaseDocument;
}

/** Response from DELETE /api/knowledge-base/:id */
export interface KnowledgeBaseDeleteResult {
  deleted: boolean;
}

/** Response from GET /api/files/:fileId */
export interface SignedUrlResponse {
  url: string;
  expiresIn: number;
}
